import type { ContractDetail, ContractSearchResult } from '../types';
import { ContractsTable } from './ContractsTable';

interface SearchResultsDisplayProps {
  /** The search results data from the API */
  data: ContractSearchResult;
  /** Set of currently selected contract numbers */
  selectedContracts: Set<string>;
  /** Handler for contract selection changes */
  onContractSelectionChange: (
    contractNumber: string,
    isSelected: boolean
  ) => void;
  /** Selection status for the "select all" checkbox */
  isAllOrNoneSelected: 'all' | 'none' | 'some';
  /** Array of contracts to display (may be filtered) */
  displayedContracts: ContractDetail[];
  /** Handler for viewing contract details */
  onViewDetails?: (contract: ContractDetail) => void;
}

/**
 * Component for displaying contract search results.
 *
 * Handles different search result states and renders the appropriate UI:
 * - Success with contracts: Shows contracts table with search results
 * - Success without contracts: Shows appropriate empty state message
 * - Cash deal: Shows notice about cash deal with optional related contracts
 *
 * @param props Configuration object for search results display
 */
export function SearchResultsDisplay({
  data,
  selectedContracts,
  onContractSelectionChange,
  isAllOrNoneSelected,
  displayedContracts,
  onViewDetails,
}: SearchResultsDisplayProps) {
  if (data.status === 'success') {
    const hasContracts = data.contracts.length > 0;

    return (
      <div className='mt-6'>
        {data.isSearchedContractNonCancellable &&
          data.nonCancellableContractNumber && (
            <NonCancellableContractNotice
              contractNumber={data.nonCancellableContractNumber}
              hasRelatedContracts={hasContracts}
            />
          )}
        {hasContracts ? (
          <>
            <h3 className='text-xl font-semibold mb-3'>Contracts Found:</h3>
            <ContractsTable
              contracts={displayedContracts}
              selectedContracts={selectedContracts}
              onContractSelectionChange={onContractSelectionChange}
              isAllOrNoneSelected={isAllOrNoneSelected}
              onViewDetails={onViewDetails}
            />
          </>
        ) : (
          <p>No contracts found matching your criteria.</p>
        )}
      </div>
    );
  }

  return null;
}

interface NonCancellableContractNoticeProps {
  contractNumber: string;
  hasRelatedContracts: boolean;
}

function NonCancellableContractNotice({
  contractNumber,
  hasRelatedContracts,
}: NonCancellableContractNoticeProps) {
  return (
    <div className='non-cancellable-contract-notice' role='status'>
      <p>
        Contract {contractNumber} is not a loan or lease deal and cannot be
        cancelled by lenders.
        {hasRelatedContracts && ' Showing related contracts instead.'}
      </p>
    </div>
  );
}
